import ExcelJS from "exceljs/dist/exceljs.min.js";
import JSZip from "jszip";
import {
  ASSESSMENT_STATUS_CHART_COLORS,
  DPAT_BRAND,
  DPAT_CHART,
  TIER_CHART_COLORS,
} from "./dpatChartTheme";
import {
  buildCiRankingRows,
  buildDistrictRankingRows,
  buildRegionRankingRows,
} from "./dpatDashboardExport";

const ExcelLib = ExcelJS?.Workbook ? ExcelJS : ExcelJS.default || ExcelJS;
const NAVY = "16325A";
const CHART_DATA_SHEET = "ChartData";
const CHARTS_SHEET = "Charts";

function hex(color) {
  return String(color || DPAT_BRAND)
    .replace("#", "")
    .toUpperCase()
    .padStart(6, "0")
    .slice(0, 6);
}

function xml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function colLetter(index) {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function pairsToSeries(items, nameKey, valueKey, colorFor) {
  const rows = (items || []).filter((item) => item && item[nameKey] != null);
  return {
    categories: rows.map((item) => String(item[nameKey])),
    series: [
      {
        name: "Districts",
        values: rows.map((item) => Number(item[valueKey]) || 0),
        colors: rows.map((item, i) => hex(colorFor ? colorFor(item, i) : DPAT_BRAND)),
      },
    ],
  };
}

export function buildDashboardChartSpecs(summary = {}, workflow = {}) {
  const charts = [];

  if (summary.tierPieChart?.length) {
    charts.push({
      title: "Performance tier mix",
      type: "pie",
      ...pairsToSeries(summary.tierPieChart, "name", "value", (item) => TIER_CHART_COLORS[item.name]),
    });
  }

  if (summary.passFailChart?.length) {
    charts.push({
      title: "Pass vs below pass",
      type: "doughnut",
      ...pairsToSeries(summary.passFailChart, "name", "value", (_, i) =>
        i === 0 ? DPAT_CHART.emerald : DPAT_CHART.coral,
      ),
    });
  }

  if (summary.sdiPiChart?.length) {
    charts.push({
      title: "SDI, PI and CI national average",
      type: "col",
      ...pairsToSeries(summary.sdiPiChart, "pillar", "percent", (_, i) =>
        [DPAT_CHART.teal, DPAT_CHART.amber, DPAT_CHART.violet][i] || DPAT_BRAND,
      ),
    });
  }

  if (summary.scoreBuckets?.length) {
    charts.push({
      title: "Overall score distribution",
      type: "area",
      ...pairsToSeries(summary.scoreBuckets, "range", "count", () => DPAT_BRAND),
    });
  }

  if (summary.regionAvgChart?.length) {
    charts.push({
      title: "Regional average Final Outcome",
      type: "bar",
      ...pairsToSeries(summary.regionAvgChart, "region", "average", () => DPAT_BRAND),
    });
  }

  if (summary.complianceBands?.length) {
    charts.push({
      title: "Compliance fulfilment",
      type: "col",
      ...pairsToSeries(summary.complianceBands, "band", "count", () => DPAT_CHART.violet),
    });
  }

  if (summary.regionCoverageChart?.length) {
    charts.push({
      title: "Assessment coverage by region",
      type: "stackedCol",
      categories: summary.regionCoverageChart.map((item) => item.region),
      series: [
        {
          name: "With score",
          values: summary.regionCoverageChart.map((item) => Number(item.assessed) || 0),
          colors: [hex(DPAT_CHART.teal)],
        },
        {
          name: "No score",
          values: summary.regionCoverageChart.map((item) => Number(item.missing) || 0),
          colors: [hex(DPAT_CHART.sand)],
        },
      ],
    });
  }

  if (summary.performanceBars?.length) {
    charts.push({
      title: "Classification headcount",
      type: "col",
      ...pairsToSeries(summary.performanceBars, "tier", "count", (item) => TIER_CHART_COLORS[item.tier]),
    });
  }

  if (workflow.statusPieChart?.length) {
    charts.push({
      title: "Assessment status mix",
      type: "pie",
      ...pairsToSeries(
        workflow.statusPieChart,
        "name",
        "value",
        (item) => ASSESSMENT_STATUS_CHART_COLORS[item.name] || ASSESSMENT_STATUS_CHART_COLORS[item.key],
      ),
    });
  }

  if (workflow.pipelineBarChart?.length) {
    charts.push({
      title: "Assessment pipeline",
      type: "col",
      ...pairsToSeries(
        workflow.pipelineBarChart,
        "stage",
        "count",
        (item) => ASSESSMENT_STATUS_CHART_COLORS[item.stage] || ASSESSMENT_STATUS_CHART_COLORS[item.fillKey],
      ),
    });
  }

  return charts.filter((chart) => chart.categories.length > 0);
}

function cacheXml(kind, items) {
  const tag = kind === "str" ? "strCache" : "numCache";
  const pts = items
    .map((item, idx) => `<c:pt idx="${idx}"><c:v>${xml(item)}</c:v></c:pt>`)
    .join("");
  return `<c:${tag}><c:ptCount val="${items.length}"/>${pts}</c:${tag}>`;
}

function dPtXml(colors, count) {
  return Array.from({ length: count }, (_, idx) => {
    const color = colors?.[idx] || colors?.[0] || hex(DPAT_BRAND);
    return `<c:dPt>
      <c:idx val="${idx}"/>
      <c:bubble3D val="0"/>
      <c:spPr><a:solidFill><a:srgbClr val="${color}"/></a:solidFill><a:ln><a:noFill/></a:ln></c:spPr>
    </c:dPt>`;
  }).join("");
}

function buildChartXml(chart, formula, embedRelId) {
  const grouping = chart.type === "stackedCol" ? "stacked" : "clustered";
  const seriesXml = chart.series
    .map((serie, sIdx) => {
      const colors = serie.colors || [hex(DPAT_BRAND)];
      const pointColors =
        colors.length === chart.categories.length
          ? colors
          : chart.categories.map(() => colors[0]);
      return `<c:ser>
        <c:idx val="${sIdx}"/>
        <c:order val="${sIdx}"/>
        <c:tx><c:strRef><c:f>${xml(formula.titles[sIdx])}</c:f>${cacheXml("str", [serie.name])}</c:strRef></c:tx>
        <c:spPr><a:solidFill><a:srgbClr val="${colors[0]}"/></a:solidFill></c:spPr>
        ${chart.series.length === 1 ? dPtXml(pointColors, chart.categories.length) : ""}
        <c:cat><c:strRef><c:f>${xml(formula.categories)}</c:f>${cacheXml("str", chart.categories)}</c:strRef></c:cat>
        <c:val><c:numRef><c:f>${xml(formula.values[sIdx])}</c:f>${cacheXml("num", serie.values)}</c:numRef></c:val>
      </c:ser>`;
    })
    .join("");

  const axes = `<c:axId val="1"/><c:axId val="2"/>`;
  const catAx = `<c:catAx><c:axId val="1"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="${chart.type === "bar" ? "l" : "b"}"/><c:tickLblPos val="nextTo"/><c:crossAx val="2"/><c:crosses val="autoZero"/></c:catAx>`;
  const valAx = `<c:valAx><c:axId val="2"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="${chart.type === "bar" ? "b" : "l"}"/><c:majorGridlines/><c:tickLblPos val="nextTo"/><c:crossAx val="1"/><c:crosses val="autoZero"/></c:valAx>`;

  const plot =
    chart.type === "pie" || chart.type === "doughnut"
      ? `<c:${chart.type === "doughnut" ? "doughnutChart" : "pieChart"}>
          <c:varyColors val="1"/>
          ${seriesXml}
          ${chart.type === "doughnut" ? `<c:holeSize val="58"/>` : ""}
        </c:${chart.type === "doughnut" ? "doughnutChart" : "pieChart"}>`
      : chart.type === "bar"
        ? `<c:barChart><c:barDir val="bar"/><c:grouping val="clustered"/><c:varyColors val="0"/>${seriesXml}${axes}</c:barChart>${catAx}${valAx}`
        : chart.type === "line" || chart.type === "area"
          ? `<c:${chart.type}Chart><c:grouping val="standard"/><c:varyColors val="0"/>${seriesXml}${axes}</c:${chart.type}Chart>${catAx}${valAx}`
          : `<c:barChart><c:barDir val="col"/><c:grouping val="${grouping}"/><c:varyColors val="0"/>${seriesXml}${axes}</c:barChart>${catAx}${valAx}`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:roundedCorners val="0"/>
  <c:chart>
    <c:title>
      <c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1200" b="1"><a:solidFill><a:srgbClr val="${NAVY}"/></a:solidFill></a:defRPr></a:pPr><a:r><a:rPr lang="en-GB" sz="1200" b="1"/><a:t>${xml(chart.title)}</a:t></a:r></a:p></c:rich></c:tx>
      <c:overlay val="0"/>
    </c:title>
    <c:autoTitleDeleted val="0"/>
    <c:plotArea>
      <c:layout/>
      ${plot}
    </c:plotArea>
    <c:legend><c:legendPos val="b"/><c:overlay val="0"/></c:legend>
    <c:plotVisOnly val="1"/>
  </c:chart>
  ${embedRelId ? `<c:externalData r:id="${embedRelId}"><c:autoUpdate val="0"/></c:externalData>` : ""}
</c:chartSpace>`;
}

function styleHeaderRow(row) {
  row.font = { bold: true, color: { argb: "FFFFFFFF" }, name: "Calibri", size: 11 };
  row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${NAVY}` } };
  row.alignment = { vertical: "middle", wrapText: true };
  row.height = 22;
}

function styleSheetTable(ws, columnCount) {
  ws.views = [{ state: "frozen", ySplit: 1 }];
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: ws.rowCount, column: columnCount },
  };
  for (let c = 1; c <= columnCount; c += 1) {
    ws.getColumn(c).width = c === 2 ? 32 : 18;
  }
  ws.eachRow((row, rowNumber) => {
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFD0D7E2" } },
        left: { style: "thin", color: { argb: "FFD0D7E2" } },
        bottom: { style: "thin", color: { argb: "FFD0D7E2" } },
        right: { style: "thin", color: { argb: "FFD0D7E2" } },
      };
      if (rowNumber === 1) return;
      if (rowNumber % 2 === 0) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF4F7FB" } };
      }
    });
  });
}

function addAoASheet(workbook, name, rows) {
  const ws = workbook.addWorksheet(name, {
    properties: { tabColor: { argb: `FF${NAVY}` } },
  });
  (rows || []).forEach((row) => ws.addRow(row));
  if (ws.rowCount > 0) {
    styleHeaderRow(ws.getRow(1));
    styleSheetTable(ws, (rows?.[0] || []).length || 1);
  }
  return ws;
}

function addKpiSheet(workbook, { year, summary, districtCount }) {
  const ws = workbook.addWorksheet("Dashboard", {
    properties: { tabColor: { argb: `FF${NAVY}` } },
  });
  ws.mergeCells("A1:B1");
  ws.getCell("A1").value = `DPAT Dashboard ${year}`;
  ws.getCell("A1").font = { bold: true, size: 18, color: { argb: `FF${NAVY}` }, name: "Calibri" };
  ws.getCell("A2").value = "Final Outcome national summary";
  ws.getCell("A2").font = { italic: true, color: { argb: "FF64748B" } };

  const kpiRows = [
    ["Metric", "Value"],
    ["Districts Assemblies Assessed", `${summary?.assessedCount ?? 0} / ${districtCount || "—"}`],
    [
      "National average %",
      summary?.nationalAvg != null ? Number(summary.nationalAvg).toFixed(1) : "—",
    ],
    ["Median score %", summary?.medianPercent != null ? Number(summary.medianPercent).toFixed(1) : "—"],
    ["SDI average %", summary?.sdiAvg != null ? Number(summary.sdiAvg).toFixed(1) : "—"],
    ["PI average %", summary?.piAvg != null ? Number(summary.piAvg).toFixed(1) : "—"],
    ["CI average %", summary?.ciAvg != null ? Number(summary.ciAvg).toFixed(1) : "—"],
    ["Passed districts", summary?.passCount ?? "—"],
    ["Below pass", summary?.failCount ?? "—"],
    ["Yet to be assessed", summary?.missingCount ?? "—"],
  ];
  kpiRows.forEach((row, idx) => {
    const excelRow = ws.addRow(row);
    if (idx === 0) styleHeaderRow(excelRow);
  });
  ws.getColumn(1).width = 36;
  ws.getColumn(2).width = 28;
  ws.spliceRows(3, 0, []);
  return ws;
}

function addChartDataSheet(workbook, charts) {
  const ws = workbook.addWorksheet(CHART_DATA_SHEET, {
    properties: { tabColor: { argb: "FF0D9488" } },
  });
  const layouts = [];
  let col = 0;
  charts.forEach((chart) => {
    const startCol = col;
    const startRow = 1;
    ws.getCell(startRow, startCol + 1).value = chart.title;
    ws.getCell(startRow, startCol + 1).font = { bold: true, color: { argb: `FF${NAVY}` } };
    ws.mergeCells(startRow, startCol + 1, startRow, startCol + 1 + chart.series.length);
    ws.getCell(startRow + 1, startCol + 1).value = "Category";
    chart.series.forEach((serie, sIdx) => {
      ws.getCell(startRow + 1, startCol + 2 + sIdx).value = serie.name;
    });
    styleHeaderRow(ws.getRow(startRow + 1));
    chart.categories.forEach((category, rIdx) => {
      ws.getCell(startRow + 2 + rIdx, startCol + 1).value = category;
      chart.series.forEach((serie, sIdx) => {
        ws.getCell(startRow + 2 + rIdx, startCol + 2 + sIdx).value = Number(serie.values[rIdx]) || 0;
      });
    });
    const lastRow = startRow + 1 + chart.categories.length;
    layouts.push({
      chart,
      formula: {
        categories: `'${CHART_DATA_SHEET}'!$${colLetter(startCol)}$${startRow + 2}:$${colLetter(startCol)}$${lastRow}`,
        titles: chart.series.map(
          (_, sIdx) => `'${CHART_DATA_SHEET}'!$${colLetter(startCol + 1 + sIdx)}$${startRow + 1}`,
        ),
        values: chart.series.map(
          (_, sIdx) =>
            `'${CHART_DATA_SHEET}'!$${colLetter(startCol + 1 + sIdx)}$${startRow + 2}:$${colLetter(startCol + 1 + sIdx)}$${lastRow}`,
        ),
      },
    });
    col += Math.max(3, chart.series.length + 2);
  });
  for (let c = 1; c <= col; c += 1) ws.getColumn(c).width = 22;
  return layouts;
}

async function injectExcelCharts(buffer, layouts) {
  const zip = await JSZip.loadAsync(buffer);
  const workbookXml = await zip.file("xl/workbook.xml").async("string");
  const relsXml = await zip.file("xl/_rels/workbook.xml.rels").async("string");
  const sheetTag = workbookXml.match(/<sheet\b[^>]*name="Charts"[^>]*\/?>/);
  const rId = sheetTag?.[0]?.match(/r:id="([^"]+)"/)?.[1];
  const target = relsXml.match(new RegExp(`Id="${rId}"[^>]*Target="([^"]+)"`))?.[1];
  if (!target) return buffer;

  const sheetPath = `xl/${target.replace(/^\//, "")}`;
  let sheetXml = await zip.file(sheetPath).async("string");
  const sheetRelsPath = sheetPath.replace("worksheets/", "worksheets/_rels/") + ".rels";
  const drawingRelId = "rIdChartDrawing";

  const anchors = layouts
    .map((layout, index) => {
      const col = (index % 2) * 10;
      const row = Math.floor(index / 2) * 18;
      return `<xdr:twoCellAnchor>
        <xdr:from><xdr:col>${col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
        <xdr:to><xdr:col>${col + 9}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${row + 16}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
        <xdr:graphicFrame macro="">
          <xdr:nvGraphicFramePr>
            <xdr:cNvPr id="${index + 2}" name="${xml(layout.chart.title)}"/>
            <xdr:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></xdr:cNvGraphicFramePr>
          </xdr:nvGraphicFramePr>
          <xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>
          <a:graphic>
            <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">
              <c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${index + 1}"/>
            </a:graphicData>
          </a:graphic>
        </xdr:graphicFrame>
        <xdr:clientData/>
      </xdr:twoCellAnchor>`;
    })
    .join("");

  zip.file(
    "xl/drawings/drawing1.xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
${anchors}
</xdr:wsDr>`,
  );
  zip.file(
    "xl/drawings/_rels/drawing1.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${layouts
  .map(
    (_, index) =>
      `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart${index + 1}.xml"/>`,
  )
  .join("")}
</Relationships>`,
  );

  layouts.forEach((layout, index) => {
    zip.file(`xl/charts/chart${index + 1}.xml`, buildChartXml(layout.chart, layout.formula));
  });

  zip.file(
    sheetRelsPath,
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="${drawingRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>
</Relationships>`,
  );

  if (sheetXml.includes("</worksheet>")) {
    sheetXml = sheetXml.replace(
      "</worksheet>",
      `<drawing r:id="${drawingRelId}"/></worksheet>`,
    );
    zip.file(sheetPath, sheetXml);
  }

  let contentTypes = await zip.file("[Content_Types].xml").async("string");
  const extras = [
    `<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`,
    ...layouts.map(
      (_, index) =>
        `<Override PartName="/xl/charts/chart${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`,
    ),
  ]
    .filter((part) => !contentTypes.includes(part))
    .join("");
  contentTypes = contentTypes.replace("</Types>", `${extras}</Types>`);
  zip.file("[Content_Types].xml", contentTypes);

  return zip.generateAsync({ type: "blob" });
}

function workflowRows(workflow) {
  return [
    ["Stage", "Districts"],
    ["Yet to start", workflow?.yetToStart ?? 0],
    ["Start", workflow?.inProgress ?? 0],
    ["Pending", workflow?.pendingReview ?? 0],
    ["Reviewed", workflow?.reviewed ?? 0],
    ["Completed", workflow?.completed ?? 0],
    ["Closed", workflow?.closed ?? 0],
    ["Total", workflow?.total ?? 0],
  ];
}

export async function downloadDashboardExcel({
  year,
  summary,
  districts,
  regions,
  workflow,
  districtCount,
}) {
  const workbook = new ExcelLib.Workbook();
  workbook.creator = "DPAT Dashboard";
  workbook.created = new Date();

  addKpiSheet(workbook, { year, summary, districtCount });
  addAoASheet(workbook, "District ranking", buildDistrictRankingRows(districts));
  addAoASheet(workbook, "CI ranking", buildCiRankingRows(districts));
  addAoASheet(workbook, "Region ranking", buildRegionRankingRows(regions));
  addAoASheet(workbook, "Assessment workflow", workflowRows(workflow));

  const charts = buildDashboardChartSpecs(summary, workflow);
  const layouts = addChartDataSheet(workbook, charts);
  const chartsSheet = workbook.addWorksheet(CHARTS_SHEET, {
    properties: { tabColor: { argb: "FFD97706" } },
  });
  chartsSheet.getCell("A1").value = "Editable DPAT charts — change values on the ChartData sheet";
  chartsSheet.getCell("A1").font = { italic: true, color: { argb: "FF64748B" } };

  const raw = await workbook.xlsx.writeBuffer();
  const blob = layouts.length
    ? await injectExcelCharts(raw, layouts)
    : new Blob([raw], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
  triggerDownload(blob, `dpat-dashboard-${year}.xlsx`);
}

function wText(value) {
  return `<w:t xml:space="preserve">${xml(value)}</w:t>`;
}

function wParagraph(value, style) {
  return `<w:p>${style ? `<w:pPr><w:pStyle w:val="${style}"/></w:pPr>` : ""}<w:r><w:rPr><w:color w:val="${NAVY}"/></w:rPr>${wText(value)}</w:r></w:p>`;
}

function wCell(value, header = false) {
  const fill = header ? `<w:shd w:val="clear" w:fill="${NAVY}"/>` : "";
  const color = header ? `<w:color w:val="FFFFFF"/><w:b/>` : `<w:color w:val="${NAVY}"/>`;
  return `<w:tc>
    <w:tcPr>${fill}<w:tcW w:w="1400" w:type="dxa"/></w:tcPr>
    <w:p><w:r><w:rPr>${color}<w:sz w:val="18"/></w:rPr>${wText(value)}</w:r></w:p>
  </w:tc>`;
}

function wTable(rows) {
  if (!rows?.length) return "";
  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="5000" w:type="pct"/>
      <w:tblBorders>
        <w:top w:val="single" w:sz="4" w:color="D0D7E2"/>
        <w:left w:val="single" w:sz="4" w:color="D0D7E2"/>
        <w:bottom w:val="single" w:sz="4" w:color="D0D7E2"/>
        <w:right w:val="single" w:sz="4" w:color="D0D7E2"/>
        <w:insideH w:val="single" w:sz="4" w:color="D0D7E2"/>
        <w:insideV w:val="single" w:sz="4" w:color="D0D7E2"/>
      </w:tblBorders>
    </w:tblPr>
    ${rows
      .map(
        (row, rowIndex) =>
          `<w:tr>${row.map((cell) => wCell(cell, rowIndex === 0)).join("")}</w:tr>`,
      )
      .join("")}
  </w:tbl>
  <w:p/>`;
}

function wChartDrawing(relId, title, index) {
  return `<w:p>
    <w:r>
      <w:drawing>
        <wp:inline distT="0" distB="0" distL="0" distR="0">
          <wp:extent cx="5486400" cy="3200400"/>
          <wp:effectExtent l="0" t="0" r="0" b="0"/>
          <wp:docPr id="${index + 1}" name="${xml(title)}"/>
          <wp:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></wp:cNvGraphicFramePr>
          <a:graphic>
            <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">
              <c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="${relId}"/>
            </a:graphicData>
          </a:graphic>
        </wp:inline>
      </w:drawing>
    </w:r>
  </w:p>`;
}

async function miniChartWorkbook(chart) {
  const workbook = new ExcelLib.Workbook();
  const ws = workbook.addWorksheet("Sheet1");
  ws.addRow(["Category", ...chart.series.map((serie) => serie.name)]);
  chart.categories.forEach((category, idx) => {
    ws.addRow([category, ...chart.series.map((serie) => Number(serie.values[idx]) || 0)]);
  });
  return workbook.xlsx.writeBuffer();
}

function wordChartFormula(chart) {
  const lastRow = 1 + chart.categories.length;
  return {
    categories: `Sheet1!$A$2:$A$${lastRow}`,
    titles: chart.series.map((_, sIdx) => `Sheet1!$${colLetter(sIdx + 1)}$1`),
    values: chart.series.map((_, sIdx) => `Sheet1!$${colLetter(sIdx + 1)}$2:$${colLetter(sIdx + 1)}$${lastRow}`),
  };
}

export async function downloadDashboardWord({
  year,
  summary,
  districts,
  regions,
  workflow,
  districtCount,
}) {
  const charts = buildDashboardChartSpecs(summary, workflow);
  const kpiRows = [
    ["Metric", "Value"],
    ["Districts Assemblies Assessed", `${summary?.assessedCount ?? 0} / ${districtCount || "—"}`],
    [
      "National average %",
      summary?.nationalAvg != null ? Number(summary.nationalAvg).toFixed(1) : "—",
    ],
    ["Median score %", summary?.medianPercent != null ? Number(summary.medianPercent).toFixed(1) : "—"],
    ["SDI average %", summary?.sdiAvg != null ? Number(summary.sdiAvg).toFixed(1) : "—"],
    ["PI average %", summary?.piAvg != null ? Number(summary.piAvg).toFixed(1) : "—"],
    ["CI average %", summary?.ciAvg != null ? Number(summary.ciAvg).toFixed(1) : "—"],
    ["Passed districts", summary?.passCount ?? "—"],
    ["Below pass", summary?.failCount ?? "—"],
    ["Yet to be assessed", summary?.missingCount ?? "—"],
  ];

  const zip = new JSZip();
  const chartRels = charts
    .map((_, index) => `<Relationship Id="rIdChart${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="charts/chart${index + 1}.xml"/>`)
    .join("");

  const chartDrawings = charts
    .map((chart, index) => wChartDrawing(`rIdChart${index + 1}`, chart.title, index))
    .join("");

  zip.file(
    "word/document.xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart">
  <w:body>
    ${wParagraph(`DPAT Dashboard ${year}`, "Title")}
    ${wParagraph("Final Outcome national summary. Charts are editable Office graphs. Buttons and filters are not included.")}
    ${wParagraph("Key performance indicators", "Heading1")}
    ${wTable(kpiRows)}
    ${wParagraph("Performance analytics", "Heading1")}
    ${chartDrawings}
    ${wParagraph("National district ranking", "Heading1")}
    ${wTable(buildDistrictRankingRows(districts))}
    ${wParagraph("National Compliance Indicators (CI) Ranking", "Heading1")}
    ${wTable(buildCiRankingRows(districts))}
    ${wParagraph("National region ranking", "Heading1")}
    ${wTable(buildRegionRankingRows(regions))}
    ${wParagraph("Assessment workflow", "Heading1")}
    ${wTable(workflowRows(workflow))}
    <w:sectPr><w:pgSz w:w="16838" w:h="11906" w:orient="landscape"/><w:pgMar w:top="720" w:right="720" w:bottom="720" w:left="720"/></w:sectPr>
  </w:body>
</w:document>`,
  );

  zip.file(
    "word/_rels/document.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  ${chartRels}
</Relationships>`,
  );

  zip.file(
    "word/styles.xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:pPr><w:spacing w:after="200"/></w:pPr><w:rPr><w:b/><w:sz w:val="40"/><w:color w:val="${NAVY}"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:pPr><w:spacing w:before="280" w:after="120"/></w:pPr><w:rPr><w:b/><w:sz w:val="28"/><w:color w:val="${NAVY}"/></w:rPr></w:style>
</w:styles>`,
  );

  await Promise.all(
    charts.map(async (chart, index) => {
      const n = index + 1;
      zip.file(`word/charts/chart${n}.xml`, buildChartXml(chart, wordChartFormula(chart), "rId1"));
      zip.file(
        `word/charts/_rels/chart${n}.xml.rels`,
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/package" Target="../embeddings/Microsoft_Excel_Worksheet${n}.xlsx"/>
</Relationships>`,
      );
      zip.file(`word/embeddings/Microsoft_Excel_Worksheet${n}.xlsx`, await miniChartWorkbook(chart));
    }),
  );

  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Default Extension="xlsx" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  ${charts
    .map(
      (_, index) =>
        `<Override PartName="/word/charts/chart${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`,
    )
    .join("")}
</Types>`,
  );

  zip.file(
    "_rels/.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
  );

  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, `dpat-dashboard-${year}.docx`);
}
